/**
 * SKE-5376. Phone publishing on an Instagram account whose posts go through
 * the API: the approved copy, the phone login state, and which posts still
 * need the phone. Pure, so the account page, the post cards and their tests
 * all read one definition.
 */
import type { AccountType } from "../../shared/types/accounts";

export const PHONE_PUBLISHING_COPY = {
  sectionTitle: "Phone publishing (optional)",
  sectionWhy: (cutover: string | null) =>
    cutover
      ? `Only used until ${cutover}, for stories with a link sticker or posts with more than 10 items.`
      : "Only used for stories with a link sticker or posts with more than 10 items.",
  states: {
    working: (cutover: string | null) =>
      cutover
        ? `Your phone login is working. Posts that still need it will publish as normal until ${cutover}.`
        : "Your phone login is working. Posts that still need it will publish as normal.",
    approval: {
      before:
        "Instagram is asking you to approve this login, or two-factor is turned on. Approve it in the Instagram app, or ",
      link: "see how to set up two-factor",
      after: ".",
    },
    credentials:
      "Instagram did not accept this username and password. Update phone login to try again.",
    none: (cutover: string | null) =>
      cutover
        ? `No phone login saved. You only need one for link sticker stories or posts with more than 10 items before ${cutover}. Add phone login if you post those.`
        : "No phone login saved. You only need one for link sticker stories or posts with more than 10 items. Add phone login if you post those.",
    checking:
      "Checking your phone login with Instagram. This usually takes a minute.",
  },
  twoFactorSaveHint:
    "Saving stores the key. Click Update phone login to check it.",
  cardWarning: "Needs your phone login to publish",
  cardFix: "Fix",
} as const;

export const TWO_FACTOR_HELP_URL =
  "https://help.skedsocial.com/enable-two-factor-authentication-on-instagram";

/** The query param that opens the account page with the section expanded. */
export const PHONE_PUBLISHING_PARAM = "phonePublishing";

export function phonePublishingSectionHref(accountId: string): string {
  return `/dashboard/accounts/${encodeURIComponent(accountId)}?${PHONE_PUBLISHING_PARAM}=open`;
}

export type PhoneLoginState =
  "working" | "approval" | "credentials" | "none" | "checking" | "other";

const APPROVAL_FAIL_REASONS = new Set([
  "VERIFICATION_REQUIRED",
  "INSECURE_LOGIN_LOCATION",
  "TWO_FACTOR_ERROR",
]);
const CREDENTIAL_FAIL_REASONS = new Set([
  "INVALID_USERNAME_PASSWORD",
  "LOGIN_FAILED",
]);
const CHECKING_STATUSES = new Set(["checking", "queued", "received"]);

type PhoneLoginFields = Pick<
  AccountType,
  "hasPhoneLogin" | "insightsOnly" | "status" | "failReason"
>;

const hasNoPhoneLogin = (account: PhoneLoginFields) =>
  account.hasPhoneLogin !== true ||
  account.insightsOnly === true ||
  account.status === "insightsOnly";

export function phoneLoginState(account: PhoneLoginFields): PhoneLoginState {
  if (hasNoPhoneLogin(account)) return "none";
  if (CHECKING_STATUSES.has(String(account.status))) return "checking";
  if (account.status === "ok") return "working";
  if (APPROVAL_FAIL_REASONS.has(String(account.failReason))) return "approval";
  if (CREDENTIAL_FAIL_REASONS.has(String(account.failReason))) {
    return "credentials";
  }
  return "other";
}

/** A post that still needs the phone would not publish with this login. */
export function isPhoneLoginBroken(account: PhoneLoginFields): boolean {
  return hasNoPhoneLogin(account) || account.status !== "ok";
}

/** The subset of a post the phone rules read. */
export interface PhoneRoutedPostShape {
  type?: string | null;
  storyType?: string | null;
  publishStoryManually?: boolean | null;
  postOptions?: { isNotification?: boolean | null } | null;
  firstcomment?: string | null;
  mediaFiles?: unknown[] | null;
  storyUrl?: unknown;
  storyLabel?: unknown;
  storyStickerHeight?: unknown;
}

export type PhoneBlocker =
  | "story_sticker"
  | "carousel_too_large"
  | "story_set_too_large"
  | "first_comment_scope_missing";

const CAROUSEL_MAX_CHILDREN = 10;
const MANAGE_COMMENTS_SCOPE = "instagram_manage_comments";

/**
 * Why a post on an API account still goes to the phone login.
 *
 * MIRRORS `isPostApiExpressible` in
 * `apps/schedugram-server/src/app/services/instagram-publishing/jobType.ts`,
 * which is the authority. Change the two together. A post published by hand
 * goes out as a notification, never through the phone login, so it has none.
 */
export function phoneBlockers(
  post: PhoneRoutedPostShape,
  account: Pick<AccountType, "session" | "longSession">
): PhoneBlocker[] {
  const manual =
    post.publishStoryManually === true ||
    post.storyType === "reel" ||
    post.storyType === "igtv" ||
    post.postOptions?.isNotification === true;
  if (manual) return [];

  const blockers: PhoneBlocker[] = [];
  if (
    post.type === "story" &&
    (post.storyUrl || post.storyLabel || post.storyStickerHeight)
  ) {
    blockers.push("story_sticker");
  }

  const mediaCount = Array.isArray(post.mediaFiles)
    ? post.mediaFiles.length
    : 0;
  if (post.type === "story" && mediaCount > CAROUSEL_MAX_CHILDREN) {
    blockers.push("story_set_too_large");
  } else if (mediaCount > CAROUSEL_MAX_CHILDREN) {
    blockers.push("carousel_too_large");
  }

  const scopes = [
    ...(account.session?.scopes ?? []),
    ...(account.longSession?.scopes ?? []),
  ];
  if (
    typeof post.firstcomment === "string" &&
    post.firstcomment.trim().length > 0 &&
    !scopes.includes(MANAGE_COMMENTS_SCOPE)
  ) {
    blockers.push("first_comment_scope_missing");
  }

  return blockers;
}
